import { START_LOCATION, type Router } from 'vue-router';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { hasRouteAccess } from '@/shared/helpers/permissions.helpers';

import { dialParamNumber } from './shared/tel-link-actions';
import { dialViaZynoTalk, openZynoTalkRoute } from './zynotalk-launcher';

const isTalkPath = (path: string) => path === '/talk' || path.startsWith('/talk/');

/**
 * In the ZynoSuite mobile app, Talk is the standalone ZynoTalk app — its web screens aren't offered
 * on mobile. Any in-app navigation to a Talk route (a phone link's Text action, a `?dial=` link, …)
 * opens ZynoTalk at the matching screen instead, and the webview stays where it was.
 *
 * Only for a Talk route the user can actually reach; anything else (including a navigation that
 * lands before permissions have loaded) proceeds as normal. So does a navigation *within* Talk: if the
 * webview was opened straight onto a Talk URL, the web screens are already showing, and handing off
 * their own redirects (`/talk` → its default tab, dropping `?dial=`) would strand an empty shell.
 * Likewise back/forward onto such a screen: cancelling it would bounce every back press to ZynoTalk.
 */
export function installZynoTalkRouteHandoff(router: Router): () => void {
    return router.beforeEach((to, from) => {
        if (!MobileBridge.isWithinZynoSuiteMobile || !isTalkPath(to.path) || isTalkPath(from.path) || !hasRouteAccess(to.path)) return;
        if (from !== START_LOCATION && isHistoryTraversal(to.fullPath)) return;

        const dial = typeof to.query.dial === 'string' ? dialParamNumber(to.query.dial) : null;
        void (dial ? dialViaZynoTalk(dial) : openZynoTalkRoute(to.fullPath));

        // Stay put — unless this is the app's first navigation, which has nowhere to stay.
        return from === START_LOCATION ? '/' : false;
    });
}

/**
 * Whether the navigation under way is a back/forward. A popstate has already moved the browser onto
 * the target entry, whose vue-router state names it; a push or replace hasn't left the current one.
 */
function isHistoryTraversal(fullPath: string): boolean {
    return (window.history.state as { current?: unknown } | null)?.current === fullPath;
}
